import { Controller } from "@hotwired/stimulus";

// Generic modal controller shared by every turbo-frame-rendered modal.
//
// `wrapper` is declared but optional — no view in the codebase actually
// supplies it. The opacity transitions are nice-to-haves; the critical path
// is closing the modal by clearing the surrounding turbo-frame, which must
// still run even when the wrapper target is absent. We guard every target
// access with `has<Name>Target` so a missing optional target can't crash
// close() and leave the dialog stuck on screen.
export default class extends Controller {
  static targets = ["background", "wrapper"];

  connect() {
    requestAnimationFrame(() => {
      if (this.hasBackgroundTarget) {
        this.backgroundTarget.classList.add("opacity-100");
      }
      if (this.hasWrapperTarget) {
        this.wrapperTarget.classList.add("opacity-100");
      }
      document.querySelector("body").classList.add("overflow-hidden");
    });

    // Bind escape key handler
    document.addEventListener("keydown", this.handleKeydown);
  }

  disconnect() {
    // Clean up event listener
    document.removeEventListener("keydown", this.handleKeydown);
  }

  // Bound to instance to maintain 'this' context
  handleKeydown = (event) => {
    if (event.key === "Escape") {
      this.close();
    }
  };

  open() {
    if (this.hasBackgroundTarget) {
      this.backgroundTarget.classList.add("opacity-100");
    }
    if (this.hasWrapperTarget) {
      this.wrapperTarget.classList.add("opacity-100");
    }
  }

  close(event) {
    // Add fade out animation
    if (this.hasBackgroundTarget) {
      this.backgroundTarget.classList.remove("opacity-100");
    }
    if (this.hasWrapperTarget) {
      this.wrapperTarget.classList.remove("opacity-100");
    }
    document.querySelector("body").classList.remove("overflow-hidden");
    // Wait for animation to complete before fully closing
    setTimeout(() => {
      const frame = this.element.closest("turbo-frame");
      if (frame) {
        frame.src = undefined; // Clear the frame source
        frame.innerHTML = ""; // Clear the frame content
      } else {
        this.element.remove(); // Fallback if no frame is found
      }
    }, 300); // Match the duration in the CSS
  }
}
